// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import dashboardConfig from '../config/dashboard.config';
import type { GridItem, GridItemOptions } from '../types/dashboard.types';
import type { WidgetMeta } from '../types/widgetTypes';

export const isMapWidgetDefinition = (widgetDefinition: WidgetMeta) => {
  if (!widgetDefinition) return false;

  if (widgetDefinition.contentType === 'map') return true;

  if (Array.isArray(widgetDefinition.category) && widgetDefinition.category.includes('Maps')) {
    return true;
  }

  const combinedLabel = `${widgetDefinition.name || ''} ${widgetDefinition.displayName || ''}`;
  return /map/i.test(combinedLabel);
};

export const isChartWidgetDefinition = (widgetDefinition: WidgetMeta) => {
  if (!widgetDefinition || isMapWidgetDefinition(widgetDefinition)) return false;

  if (Array.isArray(widgetDefinition.category) && widgetDefinition.category.includes('Charts')) {
    return true;
  }

  const combinedLabel = `${widgetDefinition.name || ''} ${widgetDefinition.displayName || ''}`;
  return /chart/i.test(combinedLabel);
};

export const isConnectedDevicesListWidgetDefinition = (widgetDefinition: WidgetMeta) =>
  widgetDefinition?.name === 'ConnectedDevices';

export const getOverwatchWidgetResizeProfile = (widgetDefinition: WidgetMeta, cols: number) => {
  const name = widgetDefinition?.name;
  if (!name) return null;

  // Connected Devices list + similar device management widgets:
  // allow wider layouts while keeping the tall (1x2) behavior.
  const tallTwoRowNames = new Set<string>(dashboardConfig['tall-device-widgets']);

  if (tallTwoRowNames.has(name)) {
    return {
      minW: 1,
      maxW: Math.min(2, cols),
      minH: 1,
      maxH: 2,
      resizeHandles: ['e', 's', 'se'] as GridItem['resizeHandles'],
      disallowOneByOne: true,
    };
  }

  if (name === 'OverwatchVMS') {
    return {
      minW: Math.min(2, cols),
      maxW: Math.min(4, cols),
      minH: 1,
      maxH: 2,
      resizeHandles: ['e', 's', 'se'] as GridItem['resizeHandles'],
      disallowOneByOne: false,
    };
  }

  return null;
};

export const buildGridItemLayout = ({
  item,
  index,
  cols,
  forceSingleColumnWidth = false,
  definedWidgetData,
  canEditWidgets,
}: GridItemOptions): GridItem => {
  const rawW = Math.max(1, Number(item?.w) || 1);
  const rawH = Math.max(1, Number(item?.h) || 1);
  const widgetDefinition = definedWidgetData?.[item?.id];
  const canEditAtBreakpoint = canEditWidgets && cols > 1;
  const mapWidget = isMapWidgetDefinition(widgetDefinition);
  const chartWidget = isChartWidgetDefinition(widgetDefinition);
  const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, cols);
  const mapResizable = canEditAtBreakpoint && (item.isDraggable ?? true) && mapWidget;
  const chartResizable = canEditAtBreakpoint && (item.isDraggable ?? true) && chartWidget;
  const connectedDevicesListResizable =
    canEditAtBreakpoint &&
    (item.isDraggable ?? true) &&
    isConnectedDevicesListWidgetDefinition(widgetDefinition);
  const overwatchProfileResizable =
    canEditAtBreakpoint && (item.isDraggable ?? true) && Boolean(overwatchResizeProfile);

  const maxChartWidth = Math.min(4, cols);
  const minChartWidth = Math.min(2, cols);
  const maxConnectedDevicesListWidth = Math.min(2, cols);
  const minConnectedDevicesListWidth = 1;
  const normalizedW = forceSingleColumnWidth
    ? 1
    : overwatchProfileResizable
      ? Math.max(overwatchResizeProfile.minW, Math.min(overwatchResizeProfile.maxW, rawW))
      : connectedDevicesListResizable
        ? Math.max(minConnectedDevicesListWidth, Math.min(maxConnectedDevicesListWidth, rawW))
        : chartWidget
          ? Math.max(minChartWidth, Math.min(maxChartWidth, rawW))
          : Math.max(1, Math.min(cols, rawW));
  let normalizedH = connectedDevicesListResizable
    ? 2
    : overwatchProfileResizable
      ? Math.max(overwatchResizeProfile.minH, Math.min(overwatchResizeProfile.maxH, rawH))
      : Math.max(1, Math.min(2, rawH));

  if (
    overwatchProfileResizable &&
    overwatchResizeProfile.disallowOneByOne &&
    normalizedW === 1 &&
    normalizedH === 1
  ) {
    normalizedH = 2;
  }
  const isResizable =
    mapResizable || chartResizable || connectedDevicesListResizable || overwatchProfileResizable;

  return {
    i: String(index),
    x: item.x ?? 0,
    y: item.y ?? 0,
    w: normalizedW,
    h: normalizedH,
    isDraggable: canEditAtBreakpoint && (item.isDraggable ?? true),
    isResizable,
    resizeHandles: overwatchProfileResizable
      ? overwatchResizeProfile.resizeHandles
      : connectedDevicesListResizable
        ? ['e', 'se']
        : isResizable
          ? ['e', 's', 'se']
          : ['se'],
    minW: mapResizable
      ? 1
      : overwatchProfileResizable
        ? overwatchResizeProfile.minW
        : connectedDevicesListResizable
          ? minConnectedDevicesListWidth
          : chartResizable
            ? minChartWidth
            : normalizedW,
    maxW: mapResizable
      ? cols
      : overwatchProfileResizable
        ? overwatchResizeProfile.maxW
        : connectedDevicesListResizable
          ? maxConnectedDevicesListWidth
          : chartResizable
            ? maxChartWidth
            : normalizedW,
    minH: mapResizable
      ? 1
      : overwatchProfileResizable
        ? overwatchResizeProfile.minH
        : connectedDevicesListResizable
          ? 2
          : chartResizable
            ? 1
            : normalizedH,
    maxH: mapResizable
      ? 2
      : overwatchProfileResizable
        ? overwatchResizeProfile.maxH
        : connectedDevicesListResizable
          ? 2
          : chartResizable
            ? 2
            : normalizedH,
  };
};
